import React, { useEffect, useRef } from 'react'
import { Input, InputProps } from './ui/Input'
import {
  formatNumeral,
  registerCursorTracker,
  unformatNumeral,
} from 'cleave-zen'
import { ControllerRenderProps } from 'react-hook-form'
import useNumberFormatter from '@/hooks/useNumberFormatter'

type AmountInputProps = InputProps & {
  controllerField: ControllerRenderProps<any, any>
}

interface AmountInputComponent
  extends React.ForwardRefExoticComponent<
    AmountInputProps & React.RefAttributes<HTMLInputElement>
  > {
  isFormControl?: boolean
}

const AmountInput: AmountInputComponent = React.forwardRef<
  HTMLInputElement,
  AmountInputProps
>(({ controllerField, ...props }, ref) => {
  const inputRef = useRef(null)

  const format = useNumberFormatter({
    addParanthesesForNegative: false,
  })

  useEffect(() => {
    return registerCursorTracker({ input: inputRef.current, delimiter: ',' })
  }, [])

  return (
    <Input
      data-slot="control"
      {...controllerField}
      ref={(el) => {
        controllerField.ref(el)
        inputRef.current = el
      }}
      value={
        controllerField.value === undefined ||
        controllerField.value === null ||
        controllerField.value === 'NaN'
          ? ''
          : controllerField.value
      }
      onChange={(e) => {
        const value = e.target.value ?? ''
        const formattedValue = formatNumeral(value, {
          numeralIntegerScale: 10,
        })
        e.target.value = formattedValue
        controllerField.onChange(e)
      }}
      onBlur={(e) => {
        const value = e.target.value ?? ''
        const unformattedValue = unformatNumeral(value)
        const parsed = parseFloat(unformattedValue)
        // A non-numeric / empty blur must not write "NaN" back into the field.
        const formattedValue = Number.isFinite(parsed) ? format(unformattedValue) : ''

        e.target.value = formattedValue
        controllerField.onChange(e)
        controllerField.onBlur()
      }}
      {...props}
    />
  )
})

AmountInput.isFormControl = true

export default AmountInput
